/**
 * Per-drop rows: label + group select
 * For Media Devoted | Created By Ciro Autiero
 */

import { Tooltip } from '@/components/ui';
import { cn } from '@/lib/utils';

import type { DropLabel } from './PerDropGroupSelector.types';

interface PerDropGroupSelectorDropRowsProps {
  dropCount: number;
  dropLabels?: DropLabel[];
  groupIdsPerDrop: string[];
  defaultGroupId: string;
  options: { id: string; name: string }[];
  onGroupChange: (dropIdx: number, groupId: string) => void;
}

export function PerDropGroupSelectorDropRows({
  dropCount,
  dropLabels,
  groupIdsPerDrop,
  defaultGroupId,
  options,
  onGroupChange,
}: PerDropGroupSelectorDropRowsProps) {
  const getRowLabel = (i: number) => {
    const dl = dropLabels?.[i];
    if (dl) {
      return (
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <span className="min-w-0 flex-1 truncate whitespace-nowrap text-[10px] font-medium text-foreground" title={dl.label}>
            {dl.label}
          </span>
          <Tooltip content={dl.id} side="top">
            <span
              className="shrink-0 cursor-help rounded bg-muted/60 px-1.5 py-0.5 text-[9px] font-medium uppercase tracking-wider text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label={`ID: ${dl.id}`}
            >
              ID
            </span>
          </Tooltip>
        </div>
      );
    }
    return (
      <span className="w-12 shrink-0 text-[10px] font-medium text-muted-foreground">
        Drop {i + 1}
      </span>
    );
  };

  return (
    <div className="max-h-[140px] space-y-1 overflow-y-auto rounded-md border border-border bg-muted/20 p-2">
      {Array.from({ length: dropCount }, (_, i) => (
        <div
          key={i}
          className={cn(
            'flex items-center gap-2 rounded px-2 py-1.5 odd:bg-muted/50',
            dropLabels ? 'min-w-0' : '',
          )}
        >
          {getRowLabel(i)}
          <select
            data-testid={`per-drop-group-select-${i}`}
            className="h-7 min-w-[180px] shrink-0 rounded-md border border-border bg-background px-2 text-xs"
            value={groupIdsPerDrop[i] ?? defaultGroupId}
            onChange={(e) => onGroupChange(i, e.target.value)}
            aria-label={dropLabels?.[i] ? `Group for ${dropLabels[i]!.label}` : `Group for drop ${i + 1}`}
          >
            {options.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}
